// @ts-check

/** @file Owns the atomic local domain-mutation plus durable queue boundary. */

import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "./project-replica-contract.js"
import ReplicaMetadata from "../models/replica-metadata.js"
import Sync from "../models/sync.js"

/** @import DatabaseOperation from "velocious/build/src/database/operation.js" */

const LAMPORT_METADATA_KEY = "mutation-lamport-sequence"

/**
 * @typedef {object} QueueMutationArgs
 * @property {Record<string, unknown>} data Replay-safe mutation payload.
 * @property {import("velocious/build/src/database/record/index.js").default} resource Mutated local record.
 * @property {Record<string, unknown> | null} [baseVersion] Authoritative version seen by the writer.
 * @property {string | null} [command] Future domain-command name.
 * @property {string | null} [predecessorMutationId] Previous mutation UUID for the same intent chain.
 * @property {string} syncType Replay operation.
 */

/**
 * @typedef {object} LocalReplicaMutationContext
 * @property {DatabaseOperation["forModel"]} forModel Operation-bound typed model accessor.
 * @property {(args: QueueMutationArgs) => Promise<InstanceType<typeof Sync>>} queueMutation Durable intent writer.
 */

/**
 * Runs one local application mutation and exactly one queue insert on the same
 * pinned SQLite transaction. The callback may mutate several domain rows, but
 * it cannot commit without queueing its durable intent. The resolved promise
 * also guarantees that any pending local database writes have crossed the
 * driver's persistence boundary.
 * @template Result Callback result type.
 * @param {import("velocious/build/src/tenants/tenant-handle.js").default} tenantHandle Captured project handle.
 * @param {(context: LocalReplicaMutationContext) => Promise<Result>} callback Operation callback.
 * @returns {Promise<{result: Result, sync: InstanceType<typeof Sync>}>} Callback result and committed queue row.
 * @rejects {Error} When the callback or durable queue contract fails.
 */
export default async function runLocalReplicaMutation(tenantHandle, callback) {
  if (!tenantHandle) throw new Error("runLocalReplicaMutation requires a project tenant handle")
  if (typeof callback !== "function") throw new Error("runLocalReplicaMutation requires a callback")

  const mutation = await tenantHandle.transaction({
    databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
    name: "Local replica mutation"
  }, async (operation) => {
    /** @type {InstanceType<typeof Sync> | null} */
    let queuedSync = null

    const queueMutation = async ({baseVersion = null, command = null, data, predecessorMutationId = null, resource, syncType}) => {
      if (queuedSync) throw new Error("A local replica mutation can queue exactly one Sync row")
      if (!resource) throw new Error("queueMutation requires a resource")
      if (!resource.isPersisted()) throw new Error("queueMutation requires a persisted resource")
      if (resource.databaseOperation() !== operation) {
        throw new Error("queueMutation requires a resource owned by the current local replica mutation operation")
      }
      const resourceId = resource.id()

      if (!resourceId) throw new Error("queueMutation requires a persisted resource id")
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        throw new Error("queueMutation data must be an object")
      }
      if (typeof syncType !== "string" || syncType.length === 0) {
        throw new Error("queueMutation requires a syncType")
      }

      const modelClass = resource.getModelClass()
      const resourceType = modelClass.getModelName()
      const sequence = await nextLamportSequence(operation)

      queuedSync = await operation.forModel(Sync).create({
        baseVersion,
        command,
        data,
        predecessorMutationId,
        resourceId: String(resourceId),
        resourceType,
        sequence,
        state: "pending",
        syncType
      })

      return queuedSync
    }

    const result = await callback({
      forModel: (modelClass) => operation.forModel(modelClass),
      queueMutation
    })

    if (!queuedSync) throw new Error("Local replica mutation completed without queueing a Sync row")

    return {result, sync: queuedSync}
  })

  await tenantHandle.flush({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})

  return mutation
}

/**
 * Atomically advances the durable mutation sequence inside the owning operation.
 * @param {import("velocious/build/src/database/operation.js").default} operation Active database operation.
 * @returns {Promise<number>} Next positive safe-integer sequence.
 * @rejects {Error} When the stored sequence is invalid or exhausted.
 */
async function nextLamportSequence(operation) {
  const metadataScope = operation.forModel(ReplicaMetadata)
  let metadata = await metadataScope.findBy({key: LAMPORT_METADATA_KEY})
  const currentValue = metadata ? Number(metadata.value()) : 0

  if (!Number.isSafeInteger(currentValue) || currentValue < 0) {
    throw new Error(`Invalid local mutation Lamport sequence: ${String(metadata?.value())}`)
  }

  const nextValue = currentValue + 1

  if (!Number.isSafeInteger(nextValue)) throw new Error("Local mutation Lamport sequence exhausted")

  if (metadata) {
    metadata.setValue(String(nextValue))
    await metadata.save()
  } else {
    metadata = await metadataScope.create({key: LAMPORT_METADATA_KEY, value: String(nextValue)})
  }

  return nextValue
}
